import React, { useState, useRef, useEffect, useCallback } from 'react';
import { contentAPI, parsePaginated } from '../services/api';
import Pagination, { PAGE_SIZE } from './Pagination';

function formatApiError(err, fallback) {
  const data = err?.response?.data;
  if (!data) return err?.message || fallback;
  if (typeof data === 'string') return data;
  if (data.detail) {
    return Array.isArray(data.detail) ? data.detail.join(' ') : String(data.detail);
  }
  const parts = Object.entries(data).map(([key, val]) => {
    const message = Array.isArray(val) ? val.join(' ') : String(val);
    return `${key}: ${message}`;
  });
  return parts.length ? parts.join(' ') : (err?.message || fallback);
}

function normalizeOptionalUrl(value) {
  const trimmed = (value || '').trim();
  if (!trimmed) return '';
  const withProtocol = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  try {
    new URL(withProtocol);
    return withProtocol;
  } catch {
    return '';
  }
}

function Blogs() {
  const fileInputRef = useRef(null);
  const editFileInputRef = useRef(null);

  const [blogs, setBlogs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);
  const [formError, setFormError] = useState(null);
  const [newBlog, setNewBlog] = useState({ 
    title: '', 
    author: '', 
    category: 'Hair', 
    status: 'Published', 
    content: '',
    imageFile: null,
    imagePreview: '',
    videoUrl: ''
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [blogToDelete, setBlogToDelete] = useState(null);

  // Add state for viewing blog details
  const [viewingBlog, setViewingBlog] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showVideo, setShowVideo] = useState(false);

  // Add a function to check if a YouTube URL is valid
  const isValidYoutubeUrl = (url) => {
    if (!url) return false;
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    return regExp.test(url);
  };

  // Extract YouTube video ID from URL
  const getYoutubeVideoId = (url) => {
    if (!url) return null;
    
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    
    return (match && match[2].length === 11) ? match[2] : null;
  };

  // Fetch blogs from API
  const fetchBlogs = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await contentAPI.getTrendingBlogs({
        page: currentPage,
        pageSize: PAGE_SIZE,
        search: debouncedSearch,
      });
      const parsed = parsePaginated(response.data);
      setBlogs(parsed.results);
      setTotalCount(parsed.count);
      if (parsed.results.length === 0 && parsed.count > 0 && currentPage > 1) {
        setCurrentPage((page) => Math.max(1, page - 1));
      }
    } catch (err) {
      console.error("Error fetching trending blogs:", err);
      setError('Failed to load trending blogs');
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, debouncedSearch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const next = searchTerm.trim();
      setDebouncedSearch((prev) => {
        if (prev !== next) {
          setCurrentPage(1);
        }
        return next;
      });
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    fetchBlogs();
  }, [fetchBlogs]);

  // Handle image upload for new blog
  const handleImageUpload = (e) => {
    const files = e.target.files;
    if (files.length > 0) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewBlog({
          ...newBlog,
          imageFiles: [...(newBlog.imageFiles || []), ...files],
          imagePreview: reader.result
        });
      };
      reader.readAsDataURL(files[0]); // Preview the first image
    }
  };

  // Handle image upload for editing blog
  const handleEditImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setEditingBlog({
          ...editingBlog,
          imageFile: file,
          imageUrl: reader.result
        });
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle add blog
  const handleAddBlog = async () => {
    setIsLoading(true);
    setFormError(null);
    try {
      const formData = new FormData();
      formData.append('title', newBlog.title);
      formData.append('description', newBlog.content);
      formData.append('youtube_video_url', normalizeOptionalUrl(newBlog.videoUrl));
      
      if (newBlog.imageFiles && newBlog.imageFiles.length > 0) {
        newBlog.imageFiles.forEach(file => {
          formData.append('uploaded_images', file);
        });
      }
      
      // Log form data for debugging
      for (let pair of formData.entries()) {
        console.log(pair[0] + ': ' + pair[1]);
      }
      
      await contentAPI.addTrendingBlog(formData);
      console.log("Blog added successfully");
      
      fetchBlogs(); // Refresh the list
      setShowAddModal(false);
      setNewBlog({
        title: '',
        content: '',
        videoUrl: '',
        imageFiles: [],
        imagePreview: ''
      });
    } catch (err) {
      console.error("Error adding blog:", err);
      setFormError(formatApiError(err, 'Failed to add blog'));
    } finally {
      setIsLoading(false);
    }
  };

  // Handle edit button click
  const handleEditClick = (blog) => {
    console.log("Editing blog:", blog);
    
    setEditingBlog({
      id: blog.id,
      title: blog.title || '',
      content: blog.description || '',
      videoUrl: blog.youtube_video_url || '',
      images: blog.images || [],
      imageFiles: [],
      imagePreview: null
    });
    setShowEditModal(true);
    setFormError(null);
  };

  // Save edited blog
  const handleSaveEdit = async () => {
    setIsLoading(true);
    setFormError(null);
    try {
      console.log("Saving blog with ID:", editingBlog.id);
      
      const formData = new FormData();
      formData.append('title', editingBlog.title);
      formData.append('description', editingBlog.content);
      formData.append('youtube_video_url', normalizeOptionalUrl(editingBlog.videoUrl));
      
      // Only append images if new ones were selected
      if (editingBlog.imageFiles && editingBlog.imageFiles.length > 0) {
        editingBlog.imageFiles.forEach(file => {
          formData.append('uploaded_images', file);
        });
      }
      
      // Log form data for debugging
      for (let pair of formData.entries()) {
        console.log(pair[0] + ': ' + pair[1]);
      }
      
      const response = await contentAPI.updateTrendingBlog(editingBlog.id, formData);
      console.log("Update response:", response);
      
      // Refresh the list after successful update
      fetchBlogs();
      setShowEditModal(false);
      setEditingBlog(null);
    } catch (err) {
      console.error("Error updating blog:", err);
      setFormError(formatApiError(err, 'Failed to update blog'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteBlog = (blog) => {
    setBlogToDelete(blog);
    setShowDeleteModal(true);
  };

  const confirmDeleteBlog = async () => {
    setIsLoading(true);
    try {
      await contentAPI.deleteTrendingBlog(blogToDelete.id);
      fetchBlogs(); // Refresh the list
      setShowDeleteModal(false);
      setBlogToDelete(null);
    } catch (err) {
      console.error("Error deleting blog:", err);
      setError(`Failed to delete blog: ${err.response?.data?.detail || err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle view details click
  const handleViewDetails = (blog) => {
    setViewingBlog(blog);
    setCurrentImageIndex(0);
    setShowVideo(blog.youtube_video_url ? true : false);
    setShowViewModal(true);
  };

  // Navigate to next image
  const nextImage = () => {
    if (viewingBlog && viewingBlog.images && viewingBlog.images.length > 1) {
      setCurrentImageIndex((prevIndex) => 
        prevIndex === viewingBlog.images.length - 1 ? 0 : prevIndex + 1
      );
    }
  };

  // Navigate to previous image
  const prevImage = () => {
    if (viewingBlog && viewingBlog.images && viewingBlog.images.length > 1) {
      setCurrentImageIndex((prevIndex) => 
        prevIndex === 0 ? viewingBlog.images.length - 1 : prevIndex - 1
      );
    }
  };

  const filteredBlogs = Array.isArray(blogs) ? blogs : [];

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">Whats Trending</h1>
        <button 
          onClick={() => setShowAddModal(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          Add New Blog
        </button>
      </div>

      {/* Filters */}
      <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <input
            type="text"
            placeholder="Search by title or description..."
            className="w-full p-2 border rounded"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Blog Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {isLoading ? (
          <div className="col-span-3 text-center py-10">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
            <p className="mt-2">Loading blogs...</p>
          </div>
        ) : error ? (
          <div className="col-span-3 text-center py-10 text-red-500">
            {error}
          </div>
        ) : filteredBlogs.length === 0 ? (
          <div className="col-span-3 text-center py-10 text-gray-500">
            No blogs found. Add your first blog post!
          </div>
        ) : (
          filteredBlogs.map((blog) => (
            <div key={blog.id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
              <div className="h-48 bg-gray-200 relative">
                {blog.images && blog.images.length > 0 ? (
                  <img 
                    src={blog.images[0].image} 
                    alt={blog.title} 
                    className="w-full h-full object-cover"
                  />
                ) : blog.youtube_video_url && isValidYoutubeUrl(blog.youtube_video_url) ? (
                  <div className="w-full h-full">
                    <iframe
                      src={`https://www.youtube.com/embed/${getYoutubeVideoId(blog.youtube_video_url)}`}
                      title={blog.title}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="w-full h-full object-cover"
                    ></iframe>
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-full bg-gray-100">
                    <svg className="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                    </svg>
                  </div>
                )}
                
                {/* Video indicator - make sure it's visible */}
                {blog.youtube_video_url && isValidYoutubeUrl(blog.youtube_video_url) && (
                  <div className="absolute top-2 right-2 bg-red-600 text-white p-1 rounded-full">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z"></path>
                    </svg>
                  </div>
                )}
                
                {/* Multiple images indicator */}
                {blog.images && blog.images.length > 1 && (
                  <div className="absolute top-2 left-2 bg-blue-600 text-white px-2 py-1 rounded-full text-xs">
                    {blog.images.length} images
                  </div>
                )}
              </div>
              <div className="p-4">
                <h3 className="text-lg font-semibold mb-2 truncate">{blog.title}</h3>
                <p className="text-gray-600 text-sm mb-3 line-clamp-2">{blog.description}</p>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-500">
                    {new Date(blog.created_at).toLocaleDateString()}
                  </span>
                  <div className="flex space-x-2">
                    <button 
                      onClick={() => handleViewDetails(blog)}
                      className="text-blue-600 hover:text-blue-900 text-sm font-medium"
                    >
                      View Details
                    </button>
                    <button 
                      onClick={() => handleEditClick(blog)}
                      className="text-blue-600 hover:text-blue-900 text-sm font-medium"
                    >
                      Edit
                    </button>
                    <button 
                      onClick={() => handleDeleteBlog(blog)}
                      className="text-red-600 hover:text-red-900 text-sm font-medium"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <div className="mt-6 rounded-lg overflow-hidden shadow">
        <Pagination page={currentPage} total={totalCount} onPageChange={setCurrentPage} />
      </div>

      {/* Add Blog Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-md w-full">
            <h2 className="text-xl font-semibold mb-4">Add New Trending Blog Post</h2>
            {formError && (
              <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded text-sm">
                {formError}
              </div>
            )}
            <div className="space-y-4">
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Title</label>
                <input
                  type="text"
                  className="w-full p-2 border rounded"
                  value={newBlog.title}
                  onChange={(e) => setNewBlog({...newBlog, title: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Content</label>
                <textarea
                  className="w-full p-2 border rounded"
                  rows="4"
                  value={newBlog.content}
                  onChange={(e) => setNewBlog({...newBlog, content: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Upload Images</label>
                <div className="flex items-center space-x-4">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current.click()}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                  >
                    Choose Images
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={handleImageUpload}
                  />
                  <span className="text-sm text-gray-500">
                    {newBlog.imageFiles && newBlog.imageFiles.length > 0 
                      ? `${newBlog.imageFiles.length} image(s) selected` 
                      : 'No files chosen'}
                  </span>
                </div>
                {newBlog.imagePreview && (
                  <div className="mt-2">
                    <img
                      src={newBlog.imagePreview}
                      alt="Preview"
                      className="h-32 object-cover rounded border"
                    />
                    {newBlog.imageFiles && newBlog.imageFiles.length > 1 && (
                      <p className="text-xs text-gray-500 mt-1">
                        +{newBlog.imageFiles.length - 1} more image(s)
                      </p>
                    )}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">YouTube Video URL</label>
                <input
                  type="text"
                  className="w-full p-2 border rounded"
                  placeholder="https://youtube.com/watch?v=..."
                  value={newBlog.videoUrl}
                  onChange={(e) => setNewBlog({...newBlog, videoUrl: e.target.value})}
                />
              </div>
            </div>
            <div className="flex justify-end mt-6">
              <button
                className="bg-gray-300 text-gray-800 px-4 py-2 rounded mr-2 hover:bg-gray-400"
                onClick={() => setShowAddModal(false)}
              >
                Cancel
              </button>
              <button
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                onClick={handleAddBlog}
                disabled={!newBlog.title || isLoading}
              >
                {isLoading ? 'Adding...' : 'Add Blog'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Blog Modal */}
      {showEditModal && editingBlog && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Edit Trending Blog Post</h2>
              <button 
                onClick={() => setShowEditModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>
            
            {formError && (
              <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded text-sm">
                {formError}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Title</label>
                <input
                  type="text"
                  className="w-full p-2 border rounded"
                  value={editingBlog.title}
                  onChange={(e) => setEditingBlog({...editingBlog, title: e.target.value})}
                  required
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Content</label>
                <textarea
                  className="w-full p-2 border rounded"
                  rows="4"
                  value={editingBlog.content}
                  onChange={(e) => setEditingBlog({...editingBlog, content: e.target.value})}
                  required
                />
              </div>
              
              {/* Current Images */}
              {editingBlog.images && editingBlog.images.length > 0 && (
                <div>
                  <label className="block text-gray-700 text-sm font-bold mb-2">Current Images</label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {editingBlog.images.map((img, index) => (
                      <div key={img.id} className="relative w-20 h-20">
                        <img
                          src={img.image}
                          alt={`Image ${index + 1}`}
                          className="w-full h-full object-cover rounded border"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Upload New Images */}
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Upload New Images</label>
                <div className="flex items-center space-x-4">
                  <button
                    type="button"
                    onClick={() => editFileInputRef.current.click()}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                  >
                    Choose Images
                  </button>
                  <input
                    ref={editFileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={(e) => {
                      const files = Array.from(e.target.files);
                      if (files.length > 0) {
                        // Create preview for first image
                        const reader = new FileReader();
                        reader.onloadend = () => {
                          setEditingBlog({
                            ...editingBlog,
                            imageFiles: [...(editingBlog.imageFiles || []), ...files],
                            imagePreview: reader.result
                          });
                        };
                        reader.readAsDataURL(files[0]);
                      }
                    }}
                  />
                </div>
                
                {/* Preview of new images */}
                {editingBlog.imageFiles && editingBlog.imageFiles.length > 0 && (
                  <div className="mt-2">
                    <p className="text-sm text-gray-700 mb-1">New images to upload:</p>
                    <div className="flex flex-wrap gap-2">
                      {editingBlog.imagePreview && (
                        <div className="relative w-20 h-20">
                          <img
                            src={editingBlog.imagePreview}
                            alt="Preview"
                            className="w-full h-full object-cover rounded border"
                          />
                          {editingBlog.imageFiles.length > 1 && (
                            <div className="absolute bottom-0 right-0 bg-black bg-opacity-70 text-white text-xs px-1 rounded">
                              +{editingBlog.imageFiles.length - 1}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">YouTube Video URL</label>
                <input
                  type="text"
                  className="w-full p-2 border rounded"
                  placeholder="https://youtube.com/watch?v=..."
                  value={editingBlog.videoUrl || ''}
                  onChange={(e) => setEditingBlog({...editingBlog, videoUrl: e.target.value})}
                />
              </div>
            </div>
            
            <div className="flex justify-end mt-6">
              <button
                className="bg-gray-300 text-gray-800 px-4 py-2 rounded mr-2 hover:bg-gray-400"
                onClick={() => setShowEditModal(false)}
                type="button"
              >
                Cancel
              </button>
              <button
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                onClick={handleSaveEdit}
                disabled={!editingBlog.title || !editingBlog.content || isLoading}
                type="button"
              >
                {isLoading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && blogToDelete && (
        <div className="fixed inset-0 overflow-y-auto z-50">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={() => setShowDeleteModal(false)}></div>
            </div>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <svg className="h-6 w-6 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Delete Blog Post
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Are you sure you want to delete "{blogToDelete.title}"? This action cannot be undone.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 hover:bg-red-700 text-base font-medium text-white focus:outline-none sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={confirmDeleteBlog}
                >
                  Delete
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => setShowDeleteModal(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Blog Details Modal */}
      {showViewModal && viewingBlog && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-semibold">{viewingBlog.title}</h2>
              <button 
                onClick={() => setShowViewModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>
            
            {/* Media viewer */}
            <div className="mb-6">
              {viewingBlog.images && viewingBlog.images.length > 0 && !showVideo && (
                <div className="relative">
                  <img 
                    src={viewingBlog.images[currentImageIndex].image} 
                    alt={`${viewingBlog.title} - Image ${currentImageIndex + 1}`}
                    className="w-full h-64 md:h-96 object-contain bg-gray-100 rounded"
                  />
                  
                  {viewingBlog.images.length > 1 && (
                    <>
                      <button 
                        onClick={prevImage}
                        className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-75 rounded-full p-2 hover:bg-opacity-100"
                      >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path>
                        </svg>
                      </button>
                      <button 
                        onClick={nextImage}
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-75 rounded-full p-2 hover:bg-opacity-100"
                      >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
                        </svg>
                      </button>
                      <div className="absolute bottom-2 left-0 right-0 flex justify-center">
                        <div className="bg-black bg-opacity-50 text-white px-3 py-1 rounded-full text-sm">
                          {currentImageIndex + 1} / {viewingBlog.images.length}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
              
              {viewingBlog.youtube_video_url && showVideo && (
                <div className="aspect-w-16 aspect-h-9">
                  <iframe
                    src={`https://www.youtube.com/embed/${getYoutubeVideoId(viewingBlog.youtube_video_url)}`}
                    title="YouTube video player"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="w-full h-64 md:h-96 rounded"
                  ></iframe>
                </div>
              )}
              
              {/* Toggle buttons */}
              {viewingBlog.youtube_video_url && viewingBlog.images && viewingBlog.images.length > 0 && (
                <div className="flex justify-center mt-4 space-x-4">
                  <button 
                    onClick={() => setShowVideo(false)}
                    className={`px-4 py-2 rounded ${!showVideo ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-800'}`}
                  >
                    Images
                  </button>
                  <button 
                    onClick={() => setShowVideo(true)}
                    className={`px-4 py-2 rounded ${showVideo ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-800'}`}
                  >
                    Video
                  </button>
                </div>
              )}
            </div>
            
            {/* Blog details */}
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-medium mb-2">Description</h3>
                <p className="text-gray-700">{viewingBlog.description}</p>
              </div>
              
              <div className="text-sm text-gray-500">
                Created: {new Date(viewingBlog.created_at).toLocaleDateString()}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Blogs;




















